import type { ReactNode } from 'react';
import type { Space } from '../../lib/flotiq-client/types/api-response';

import { useCallback, useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import useSessionStorageState from 'use-session-storage-state';

import { useUserDefaultSpaceSlug } from '../../hooks/api/useUserDefaultSpaceSlug';
import { useUserSpaces } from '../../hooks/api/useUserSpaces';

import { ModalProvider } from '../../contexts/ModalContext';

import LoaderPage from '../../pages/LoaderPage/LoaderPage';

interface NavigatePageProps {
  children?: ReactNode;
}

const NavigatePage = ({ children }: NavigatePageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { spaceSlug } = useParams();
  const { t } = useTranslation();
  const [impersonate] = useSessionStorageState('cms.impersonate');
  const toastRef = useRef<string>(null);

  const { spaces, isLoading: spacesLoading, errors } = useUserSpaces();

  const {
    defaultSpaceSlug,
    isLoading: defaultSpaceLoading,
    updateDefaultSpace,
  } = useUserDefaultSpaceSlug();

  const isLoading = spacesLoading || defaultSpaceLoading;

  useEffect(() => {
    if (errors) {
      if (toastRef.current) toast.remove(toastRef.current);
      toastRef.current = toast.error(t('Global.GetSpacesError'));
    }
  }, [errors, t]);

  const handleEntranceInSpace = useCallback(
    async (spaces: Space[], spaceSlug?: string, defaultSpaceSlug?: string) => {
      if (spaceSlug) {
        const existSpace = spaces.find((el) => el.slug === spaceSlug);
        const currentSpaceId = existSpace?.id;

        const pathnameWithoutSpace =
          (location.pathname || '').split(`/s/${spaceSlug}`)?.[1] || '';

        if (!currentSpaceId) {
          if (spaceSlug !== '__default') {
            toast.error(t('Spaces.ErrorWrongSpaceIdRedirectToDefault'));
          }

          const spaceToNavigate = defaultSpaceSlug
            ? `/s/${defaultSpaceSlug}`
            : `/s/${spaces[0].slug}`;

          navigate(
            `${spaceToNavigate}${pathnameWithoutSpace}${location.search}${location.hash}`,
          );
          return;
        }

        if (existSpace?.slug !== defaultSpaceSlug && !impersonate) {
          updateDefaultSpace(existSpace.id, existSpace.slug);
        }

        if (!impersonate) {
          TagManager.dataLayer({
            dataLayer: {
              event: 'space_changed',
              space_id: currentSpaceId,
              plan_id: existSpace?.planLimits?.id,
              plan_name: existSpace?.planLimits?.name,
            },
          });
        }
      } else if (defaultSpaceSlug) {
        navigate(`/s/${defaultSpaceSlug}`);
      } else {
        navigate(`/s/${spaces[0].slug}`);
      }
    },
    [
      location.pathname,
      location.search,
      location.hash,
      impersonate,
      t,
      navigate,
      updateDefaultSpace,
    ],
  );

  useEffect(() => {
    if (isLoading) return;

    if (!spaces?.length) {
      navigate('/start');
      return;
    }

    handleEntranceInSpace(spaces, spaceSlug, defaultSpaceSlug);
  }, [
    defaultSpaceSlug,
    handleEntranceInSpace,
    isLoading,
    navigate,
    spaceSlug,
    spaces,
  ]);

  const currentChildren = children ? children : <Outlet />;

  return (
    <ModalProvider>
      {isLoading ? <LoaderPage /> : currentChildren}
    </ModalProvider>
  );
};

export default NavigatePage;
